import type {
  Dapp,
  Publisher as PublisherDto,
  Release as ReleaseDto,
} from '@src/dto';
import { CircleAlert, FolderOpen, Smartphone } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useDappPublish } from '../../hooks/useDappPublish';
import { useDappStoreStatus } from '../../hooks/useDappStoreStatus';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePolling } from '../../hooks/usePolling';
import { useTRPC } from '../../hooks/useTRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  fetchMetadataJson,
  isMetadataUnavailableError,
} from '../../utils/metadataFetch';
import { getMetadataSolanaDappStore } from '../../utils/nftMetadataHelpers';
import {
  getReleaseManagementPermissionMessage,
  getStoreSubmissionPermissionMessage,
} from '../../utils/publicationAccess';
import { getDappReleasesPath } from '../../utils/dappRoutes';
import {
  getReleaseStatusDisplay,
  isReleaseWaitingToGoLive,
} from '../../utils/releaseStatusConfig';
import { Button } from '../Button';
import { useDappContext } from '../DappLayout';
import {
  Breadcrumb,
  PortalCard,
  PortalPage,
  PortalPageHeader,
  Skeleton,
  SKELETON_REVEAL_CLASS,
  SkeletonGroup,
  StatusPill,
} from '../portal';
import { useApkDownloadUrl } from './hooks/useApkDownloadUrl';
import {
  formatReleaseVersionTitle,
  ReleaseLoadedContent,
} from './ReleaseLoadedContent';

const NftMetadataFileSchema = z.object({
  purpose: z.string().optional(),
  uri: z.string().optional(),
});

const ReleaseNftMetadataSchema = z.object({
  image: z.string().optional(),
  animation_url: z.string().optional(),
  external_url: z.string().optional(),
  extensions: z
    .object({
      solana_dapp_store: z
        .object({
          files: z.array(NftMetadataFileSchema).optional(),
        })
        .optional(),
    })
    .optional(),
});

type ReleaseNftMetadata = z.infer<typeof ReleaseNftMetadataSchema>;
type ReleaseNftMetadataSolanaDappStore = NonNullable<
  NonNullable<ReleaseNftMetadata['extensions']>['solana_dapp_store']
>;

export function ReleaseDetailPage() {
  const trpc = useTRPC();
  const { dappId, packageName } = useDappContext();
  const { releaseId } = useParams<{ releaseId: string }>();
  const releasesPath = getDappReleasesPath({ id: dappId, packageName });
  const [release, setRelease] = useState<ReleaseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMintModal, setShowMintModal] = useState(false);
  const [dapp, setDapp] = useState<Dapp | null>(null);
  const storeStatus = useDappStoreStatus(dappId);
  const [publisher, setPublisher] = useState<PublisherDto | null>(null);
  const [arweaveApkUrl, setArweaveApkUrl] = useState<string | null>(null);
  const [nftMetadata, setNftMetadata] = useState<ReleaseNftMetadata | null>(
    null,
  );
  const loadReleaseRequestIdRef = useRef(0);
  const processingStatusRequestRef = useRef<Promise<void> | null>(null);
  const routeKey = `${dappId ?? ''}:${releaseId ?? ''}`;
  const currentRouteKeyRef = useRef(routeKey);
  currentRouteKeyRef.current = routeKey;

  const apkDownloadUrl = useApkDownloadUrl(release?.releaseFileUrl);
  const { isPublishing, handlePublish } = useDappPublish(dappId, () => {
    void loadRelease();
    storeStatus.refetch();
  });
  const releaseManagementPermissionMessage =
    getReleaseManagementPermissionMessage(dapp);
  const storeSubmissionPermissionMessage =
    getStoreSubmissionPermissionMessage(dapp);
  const canManageReleaseArtifacts = !releaseManagementPermissionMessage;
  const canSubmitReleaseToStore = !storeSubmissionPermissionMessage;
  const title = release ? formatReleaseVersionTitle(release.versionName) : null;

  usePageTitle(
    title
      ? `${title} - Release | Publishing Portal`
      : 'Release | Publishing Portal',
  );

  useEffect(() => {
    return () => {
      loadReleaseRequestIdRef.current += 1;
      processingStatusRequestRef.current = null;
      currentRouteKeyRef.current = '';
    };
  }, [routeKey]);

  useEffect(() => {
    if (releaseId) {
      // Drop the previous release so the skeleton shows instead of stale data.
      setRelease(null);
      setDapp(null);
      setError(null);
      loadRelease();
    }
  }, [releaseId]);

  useEffect(() => {
    let isCancelled = false;

    const loadNftMetadata = async () => {
      if (!release?.nftMetadataUri) {
        setNftMetadata(null);
        setArweaveApkUrl(null);
        return;
      }

      try {
        const metadata = await fetchMetadataJson(release.nftMetadataUri);
        if (isCancelled) {
          return;
        }

        const parseResult = ReleaseNftMetadataSchema.safeParse(metadata);
        if (!parseResult.success) {
          captureAndReportError(
            'Error parsing NFT metadata:',
            parseResult.error.message,
          );
          setNftMetadata(null);
          setArweaveApkUrl(null);
          return;
        }

        setNftMetadata(parseResult.data);

        const solanaDappStore =
          getMetadataSolanaDappStore<ReleaseNftMetadataSolanaDappStore>(
            parseResult.data,
          );
        const apkFile = solanaDappStore?.files?.find(
          (file) => file.purpose === 'install',
        );
        setArweaveApkUrl(apkFile?.uri ?? null);
      } catch (err) {
        if (isCancelled) {
          return;
        }
        if (isMetadataUnavailableError(err)) {
          console.warn('NFT metadata is not available', {
            metadataUri: release.nftMetadataUri,
            releaseId: release.id,
            error: err,
          });
        } else {
          captureAndReportError(err, {
            operation: 'Error fetching NFT metadata',
            metadataUri: release.nftMetadataUri,
            releaseId: release.id,
          });
        }
        setNftMetadata(null);
        setArweaveApkUrl(null);
      }
    };

    loadNftMetadata();

    return () => {
      isCancelled = true;
    };
  }, [release?.id, release?.nftMetadataUri]);

  // Check on-chain collection verification status and sync DB if needed
  useEffect(() => {
    let isCancelled = false;
    const requestRouteKey = `${dappId ?? ''}:${releaseId ?? ''}`;
    const isCurrentRequest = () =>
      !isCancelled && currentRouteKeyRef.current === requestRouteKey;

    const checkAndSyncCollectionStatus = async () => {
      if (
        dapp?.canManageReleases !== true ||
        !release?.nftMintAddress ||
        release.isCollectionVerified ||
        !releaseId
      ) {
        return;
      }

      try {
        const result = await trpc.release.checkOnChainCollectionStatus.query({
          releaseId,
        });
        if (!isCurrentRequest()) {
          return;
        }

        if (result._tag === 'Right' && result.right.isVerifiedOnChain) {
          await trpc.release.markReleaseCollectionAsVerified.mutate({
            releaseId,
          });
          if (!isCurrentRequest()) {
            return;
          }
          setRelease((prev) =>
            prev ? { ...prev, isCollectionVerified: true } : prev,
          );
          toast.info('Collection verification status synced from blockchain');
        }
      } catch (err) {
        // Don't show error to user, this is a background sync
        captureAndReportError(
          'Error checking on-chain collection status:',
          err,
        );
      }
    };

    checkAndSyncCollectionStatus();

    return () => {
      isCancelled = true;
    };
  }, [
    dapp?.canManageReleases,
    release?.nftMintAddress,
    release?.isCollectionVerified,
    releaseId,
  ]);

  const loadRelease = async () => {
    if (!releaseId || !dappId) return;

    const requestId = loadReleaseRequestIdRef.current + 1;
    loadReleaseRequestIdRef.current = requestId;
    const requestRouteKey = `${dappId}:${releaseId}`;
    const isCurrentRequest = () =>
      loadReleaseRequestIdRef.current === requestId &&
      currentRouteKeyRef.current === requestRouteKey;

    try {
      setLoading(true);

      const result = await trpc.release.getReleaseForDapp.query({
        dappId,
        releaseId,
      });
      if (!isCurrentRequest()) {
        return;
      }

      if (result._tag === 'Left') {
        setError(result.left.message);
        toast.error('Failed to load release');
      } else {
        setError(null);
        setRelease(result.right);

        if (result.right.status === 'Processing') {
          void loadProcessingStatus({
            processingJobId: result.right.processingJobId,
            currentStatus: result.right.status,
          });
        }

        const dappResult = await trpc.getDapp.query({ dappId });
        if (!isCurrentRequest()) {
          return;
        }

        if (dappResult._tag === 'Right') {
          setDapp(dappResult.right);
        }

        const publisherResult =
          await trpc.getCurrentUserPublisher.query(undefined);
        if (!isCurrentRequest()) {
          return;
        }

        if (publisherResult._tag === 'Right') {
          setPublisher(publisherResult.right);
        }
      }
    } catch (err) {
      if (!isCurrentRequest()) {
        return;
      }

      setError('Failed to load release');
      captureAndReportError('Error loading release:', err);
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
      }
    }
  };

  const loadProcessingStatus = async ({
    processingJobId,
    currentStatus,
  }: {
    processingJobId?: string | null;
    currentStatus?: ReleaseDto['status'];
  } = {}) => {
    if (!releaseId) return;

    const requestRouteKey = `${dappId ?? ''}:${releaseId}`;
    if (currentRouteKeyRef.current !== requestRouteKey) {
      return;
    }

    if (processingStatusRequestRef.current) {
      return processingStatusRequestRef.current;
    }

    let request!: Promise<void>;
    request = (async () => {
      try {
        const result = await trpc.release.getReleaseProcessingStatus.query({
          releaseId,
          processingJobId: processingJobId ?? release?.processingJobId,
        });
        if (currentRouteKeyRef.current !== requestRouteKey) {
          return;
        }

        if (result._tag === 'Right') {
          if (result.right.status !== (currentStatus ?? release?.status)) {
            await loadRelease();
          }
        }
      } catch (err) {
        if (currentRouteKeyRef.current !== requestRouteKey) {
          return;
        }

        captureAndReportError('Error loading processing status:', err);
      } finally {
        if (processingStatusRequestRef.current === request) {
          processingStatusRequestRef.current = null;
        }
      }
    })();

    processingStatusRequestRef.current = request;
    return request;
  };

  usePolling(loadProcessingStatus, 2000, {
    enabled: release?.status === 'Processing',
    preventOverlap: true,
  });

  const handleMintSuccess = (mintAddress: string, txSignature: string) => {
    setRelease((prev) =>
      prev
        ? {
            ...prev,
            nftMintAddress: mintAddress,
            nftTransactionSignature: txSignature,
          }
        : prev,
    );

    setShowMintModal(false);

    toast.success('Release NFT minted successfully!', {
      description: `Transaction: ${txSignature.substring(0, 20)}...`,
    });
  };

  const handleVerificationSuccess = async () => {
    await loadRelease();

    if (!canSubmitReleaseToStore) {
      if (storeSubmissionPermissionMessage) {
        toast.info(storeSubmissionPermissionMessage, {
          duration: 6000,
        });
      }
      return;
    }

    if (release && release.submissionStatus === 'NotSubmitted') {
      try {
        toast.info('Submitting release to store...', { duration: 5000 });

        const submitToStoreResult = await trpc.release.submitToStore.mutate({
          releaseId: release.id,
          whatsNew: release.newInVersion || '',
          criticalUpdate: false,
        });

        if (
          submitToStoreResult._tag === 'Right' &&
          submitToStoreResult.right.success
        ) {
          toast.success(submitToStoreResult.right.message, {
            description: submitToStoreResult.right.hubspotTicketId
              ? `Ticket ID: ${submitToStoreResult.right.hubspotTicketId}`
              : undefined,
            duration: 8000,
          });
          await loadRelease();
        } else {
          toast.error('Failed to submit release to store', {
            description: 'Please try again or contact support',
          });
        }
      } catch (submitError) {
        captureAndReportError('Failed to submit to store:', submitError);
        toast.error('Failed to submit release to store', {
          description: 'Please try again or contact support',
        });
      }
    }
  };

  const statusContext = {
    publishedVersionCode: dapp?.publishedVersionCode,
    storeStatus: storeStatus.storeStatus,
  };
  const statusDisplay = release
    ? getReleaseStatusDisplay(release, statusContext)
    : null;
  const StatusIcon = statusDisplay?.icon;
  const showGoLive =
    release != null &&
    storeStatus.canPublish &&
    isReleaseWaitingToGoLive(release, statusContext);
  // Only the very first load shows skeletons; refetches update in place.
  // Wait for the Store lookup too, so the status pill never flips Live → Waiting.
  const isInitialLoad = (loading && !release) || !storeStatus.hasResolved;

  let body: ReactNode;
  if (release && storeStatus.hasResolved) {
    body = (
      <ReleaseLoadedContent
        release={release}
        dapp={dapp}
        publisher={publisher}
        nftMetadata={nftMetadata}
        apkDownloadUrl={apkDownloadUrl}
        arweaveApkUrl={arweaveApkUrl}
        canManageReleaseArtifacts={canManageReleaseArtifacts}
        canSubmitReleaseToStore={canSubmitReleaseToStore}
        showMintModal={showMintModal}
        onReloadRelease={loadRelease}
        onMintClick={() => setShowMintModal(true)}
        onMintClose={() => setShowMintModal(false)}
        onMintSuccess={handleMintSuccess}
        onVerificationSuccess={handleVerificationSuccess}
      />
    );
  } else if (isInitialLoad) {
    body = <ReleaseDetailSkeleton />;
  } else if (error) {
    body = (
      <EmptyState
        tone="error"
        icon={<CircleAlert className="size-5 text-rose-400" />}
        title="Couldn't load release"
        description={error}
      />
    );
  } else {
    body = (
      <EmptyState
        icon={<FolderOpen className="size-5 text-seeker-400" />}
        title="Release not found"
        description="This release may have been removed or you may not have access."
      />
    );
  }

  // Facts page: a narrower measure keeps each value next to its copy/open actions.
  return (
    <PortalPage className="bg-seeker-900" contentClassName="max-w-[960px]">
      <PortalPageHeader
        breadcrumb={
          <Breadcrumb
            items={[
              { label: 'Releases', to: releasesPath, icon: <Smartphone /> },
              {
                label: isInitialLoad ? (
                  <Skeleton className={`h-3.5 w-14 ${SKELETON_REVEAL_CLASS}`} />
                ) : (
                  (title ?? 'Release')
                ),
              },
            ]}
          />
        }
        title={
          isInitialLoad ? (
            <Skeleton className={`h-10 w-36 ${SKELETON_REVEAL_CLASS}`} />
          ) : (
            (title ?? 'Release')
          )
        }
        titleAdornment={
          isInitialLoad ? (
            <Skeleton
              className={`h-5 w-28 ${SKELETON_REVEAL_CLASS}`}
              rounded="full"
            />
          ) : statusDisplay && StatusIcon ? (
            <StatusPill
              tone={statusDisplay.pillTone}
              icon={<StatusIcon className={statusDisplay.iconClassName} />}
            >
              {statusDisplay.label}
            </StatusPill>
          ) : undefined
        }
        actions={
          showGoLive ? (
            <Button
              variant="primaryBlue"
              className="w-full min-w-[180px] sm:w-auto"
              disabled={isPublishing}
              onClick={handlePublish}
            >
              {isPublishing ? 'Publishing…' : 'Go Live Now'}
            </Button>
          ) : undefined
        }
      />
      {body}
    </PortalPage>
  );
}

/**
 * Mirrors `BuildSection` and `NftInformationSection` 1:1 (same paddings, row
 * heights and grid) so the cards do not move when the release lands.
 */
function ReleaseDetailSkeleton() {
  return (
    <SkeletonGroup label="Loading release" className="flex flex-col gap-4">
      <PortalCard className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex h-10 flex-wrap items-center gap-x-6 gap-y-1">
            <Skeleton className="h-3.5 w-36" />
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="h-3.5 w-24" />
          </div>
          <Skeleton className="h-10 w-full rounded-xl sm:w-[150px]" />
        </div>
        <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4 border-b border-seeker-800 pb-5">
          <div className="flex min-w-0 flex-col">
            <Skeleton className="h-6 w-64 max-w-full" />
            <span className="mt-0.5 flex h-4 items-center">
              <Skeleton className="h-3 w-32" />
            </span>
          </div>
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="mt-5 grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex flex-col gap-1">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-6 w-40 max-w-full" />
            </div>
          ))}
          <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-3">
            <Skeleton className="h-4 w-20" />
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Skeleton key={index} className="h-6 w-14" rounded="md" />
              ))}
            </div>
          </div>
        </div>
        <div className="-mx-6 -mb-6 mt-6 flex items-center justify-between gap-4 border-t border-seeker-800 px-6 py-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="size-4" />
        </div>
      </PortalCard>
      <PortalCard className="p-6">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" rounded="full" />
        </div>
        <div className="mt-5 divide-y divide-seeker-800">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="grid grid-cols-1 items-center gap-y-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6"
            >
              <Skeleton className="h-4 w-24" />
              <span className="flex h-8 items-center">
                <Skeleton className="h-6 w-56 max-w-full" />
              </span>
            </div>
          ))}
        </div>
      </PortalCard>
    </SkeletonGroup>
  );
}

interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  description: string;
  tone?: 'neutral' | 'error';
}

function EmptyState({
  icon,
  title,
  description,
  tone = 'neutral',
}: EmptyStateProps) {
  return (
    <PortalCard className="flex flex-col items-center px-8 py-16 text-center">
      <div
        className={`mb-4 flex size-10 items-center justify-center rounded-full border ${
          tone === 'error'
            ? 'border-rose-500/20 bg-rose-500/10'
            : 'border-seeker-700 bg-seeker-800'
        }`}
      >
        {icon}
      </div>
      <h3 className="text-sm font-bold text-white">{title}</h3>
      <p className="mt-1.5 max-w-sm text-sm text-seeker-400">{description}</p>
    </PortalCard>
  );
}
